import { useEffect, useMemo, useState } from 'react';
import { Card } from 'vopay';
import { Title, UtilButton, Skeleton } from 'UI';
import { useTranslation } from 'react-i18next';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import type { UserRoles } from 'vopay';
import ApproversDetails from './ApproversDetails';
import ApproversEdit from './ApproversEdit';
import styles from './Approvers.module.scss';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
interface ApproversProps {
  isDisabled?: boolean;
  onEdit: (isEditing: boolean) => void;
}

const Approvers = ({ isDisabled = false, onEdit }: ApproversProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Roles.Approvers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { response, fetchUserRoles } = useFetchUserRoles();
  const [isEditing, setIsEditing] = useState(false);
  const [, setLoading] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const { workflows, fetchWorkflows, fetchingWorkflows } = useFetchWorkflows();

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        setLoading(true);
        await fetchUserRoles({ Role: 'approver' });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('loadErrorMessage', { defaultValue: 'Failed to load approvers. Please try again.' });
        await alertDialog.error(t('loadErrorTitle', { defaultValue: 'Error' }), errorMessage, {
          abortLabel: t('closeButton', { defaultValue: 'Close' }),
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserRoles();
    refreshWorkflows();
  }, []);

  const approvers = response?.UserRolePermissions || [];

  const activeTransactionTypeIDs = useMemo(() => {
    return workflows
      .filter((group) => group.Workflows.some((wf) => wf.IsActive))
      .map((group) => group.TransactionType);
  }, [workflows]);

  // Group approvers by username
  const groupedApprovers = useMemo(() => {
    if (!response?.UserRolePermissions) return {};

    const grouped: Record<string, UserRoles[]> = {};

    response.UserRolePermissions.forEach((userRole) => {
      if (!grouped[userRole.Username]) {
        grouped[userRole.Username] = [];
      }
      grouped[userRole.Username].push(userRole);
    });

    return grouped;
  }, [response?.UserRolePermissions]);

  const handleEdit = () => {
    setIsEditing(true);
    onEdit(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    onEdit(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Refresh the list after save
      await fetchUserRoles({ Role: 'approver' });
      setIsEditing(false);
      onEdit(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('refreshErrorMessage', { defaultValue: 'Failed to refresh approvers. Please reload the page.' });
      await alertDialog.error(t('refreshErrorTitle', { defaultValue: 'Error' }), errorMessage, {
        abortLabel: t('closeButton', { defaultValue: 'Close' }),
      });
    } finally {
      setLoading(false);
    }
  };

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch (err) {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }

  return (
    <Card className={styles.card} style={{ opacity: isDisabled ? 0.5 : 1 }}>
      <div className={styles.header}>
        <Title level="4">{t('title', { defaultValue: 'Approvers' })}</Title>

        {fetchingWorkflows ? (
          <div className={styles.editSkeleton}>
            <Skeleton height="100%" />
          </div>
        ) : (
          !isEditing && (
            <UtilButton onClick={handleEdit} iconLeft="edit" disabled={isDisabled}>
              {t('editButton', { defaultValue: 'Edit Approvers' })}
            </UtilButton>
          )
        )}
      </div>

      {isEditing ? (
        <ApproversEdit
          approvers={approvers}
          activeTransactionTypeIDs={activeTransactionTypeIDs}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ) : (
        <ApproversDetails approvers={groupedApprovers} />
      )}
    </Card>
  );
};

export default Approvers;
